/**
 * Triage content — an interrogation tool. The user describes a mechanic, system,
 * or feature and answers probing questions grouped into "lenses" so gaps surface.
 * Questions are original, distilled from the study library's frameworks; each lens
 * links to a Library entry (Study this) and a discipline (Practice / mastery).
 */

export type TriageType = "mechanic" | "system" | "feature";

export interface TriageTypeInfo {
  id: TriageType;
  name: string;
  blurb: string;
}

export const TRIAGE_TYPES: TriageTypeInfo[] = [
  { id: "mechanic", name: "Mechanic", blurb: "A single rule or action the player performs — a verb: dash, hack, build, parry." },
  { id: "system", name: "System", blurb: "Interacting rules that produce behavior over time — an economy, progression, an AI director." },
  { id: "feature", name: "Feature", blurb: "A player-facing capability or mode — a shop, a crafting screen, a season, a co-op mode." },
];

export interface TriageLens {
  id: string;
  name: string;
  blurb: string;
  appliesTo: TriageType[];
  questions: string[];
  /** "Study this" cross-link into the Library. */
  libraryEntryId?: string;
  /** "Practice this" + mastery credit. */
  disciplineId?: string;
}

export const TRIAGE_LENSES: TriageLens[] = [
  {
    id: "purpose",
    name: "Purpose & Core",
    blurb: "Why it exists and the one experience it's built around.",
    appliesTo: ["mechanic", "system", "feature"],
    libraryEntryId: "lib-mda",
    disciplineId: "systems",
    questions: [
      "What single emotion or experience should this create for the player? Name it specifically — not just \"fun\".",
      "What is the core verb or action at its heart, stated in one word or phrase?",
      "Why does this exist — what problem, for the player or the design, does it solve?",
      "If you removed it entirely, what exactly would the game lose?",
      "Does every part of it serve that one purpose, or is something along for the ride?",
    ],
  },
  {
    id: "decisions",
    name: "Player Decisions & Depth",
    blurb: "The interesting choices it puts in front of the player.",
    appliesTo: ["mechanic", "system"],
    libraryEntryId: "lib-meaningful-decisions",
    disciplineId: "systems",
    questions: [
      "What interesting decision does this put in front of the player?",
      "What is the trade-off — what does each option give up?",
      "Is there a dominant option that's always correct? If so, why would anyone choose otherwise?",
      "Does the decision stay interesting on the tenth use, or is it solved after the first?",
      "Does the player have enough information to reason about the choice, but not so much that it's obvious?",
    ],
  },
  {
    id: "economy",
    name: "Systems & Economy",
    blurb: "The resources that flow through it and how they're balanced.",
    appliesTo: ["system", "feature"],
    libraryEntryId: "lib-internal-economy",
    disciplineId: "economy",
    questions: [
      "What resources flow through this? Include intangible ones — time, tempo, attention, risk, aggro.",
      "Where does each resource come from (sources) and where does it go (drains)?",
      "Is every source matched by a drain, or can something accumulate or starve without limit?",
      "What's the dominant loop a player will optimize — and is it the fun one?",
      "Could a determined player break this economy in week three?",
    ],
  },
  {
    id: "loops",
    name: "Feedback Loops & Balance",
    blurb: "Snowballing vs stabilizing forces, and fairness.",
    appliesTo: ["system"],
    libraryEntryId: "lib-feedback-loops",
    disciplineId: "economy",
    questions: [
      "Where are the positive (snowballing) and negative (stabilizing) feedback loops?",
      "Does a lead snowball until the loser feels hopeless? Does catch-up erase earned advantage?",
      "Are options priced on a cost curve (transitive) or matched like rock-paper-scissors (intransitive)?",
      "Is there a dominant strategy that collapses the space of viable choices?",
      "What happens at the extremes — a huge lead, a total deficit?",
    ],
  },
  {
    id: "clarity",
    name: "Feedback & Clarity",
    blurb: "Whether the player can read what's happening and why.",
    appliesTo: ["mechanic", "system", "feature"],
    libraryEntryId: "lib-clarity",
    disciplineId: "ux",
    questions: [
      "Can the player perceive the result of what they did (discernible), and does it matter later (integrated)?",
      "What builds the player's mental model of how this works — and could that model end up wrong?",
      "Is the most important thing here also the most visually or audibly prominent? (Try the grayscale test.)",
      "How is a change in state communicated without text?",
      "When the player fails, do they understand why?",
    ],
  },
  {
    id: "feel",
    name: "Game Feel",
    blurb: "The tactile quality of the moment-to-moment action.",
    appliesTo: ["mechanic"],
    libraryEntryId: "lib-game-feel-metrics",
    disciplineId: "game-feel",
    questions: [
      "What does the input-to-response chain feel like — tight or floaty? Roughly how much delay?",
      "What's the feedback stack on the key action — visual, audio, haptic, timing?",
      "Does it still feel good on the ten-thousandth repetition, or does it fatigue?",
      "What weight or commitment does the action carry — wind-up, active window, recovery?",
      "If it feels wrong, which single metric would you tune first?",
    ],
  },
  {
    id: "progression",
    name: "Progression & Mastery",
    blurb: "How the player learns, grows, and keeps coming back.",
    appliesTo: ["system", "feature"],
    libraryEntryId: "lib-fun-is-learning",
    disciplineId: "progression",
    questions: [
      "What new pattern does the player learn here, and what keeps fresh patterns coming?",
      "Does the player grow through skill or through numbers — and is that what you intend?",
      "Where does this sit on the challenge-vs-skill curve — could it bore or overwhelm?",
      "What's the reason to come back tomorrow?",
      "How is mastery made visible and rewarding for an expert?",
    ],
  },
  {
    id: "emergence",
    name: "Emergence & Interactions",
    blurb: "How it combines with other systems — for better or worse.",
    appliesTo: ["mechanic", "system"],
    libraryEntryId: "lib-emergence-progression",
    disciplineId: "systems",
    questions: [
      "What other systems or mechanics does this touch, and what happens where they collide?",
      "What can a player engineer with it that you didn't explicitly design?",
      "Are there combinations that trivialize a challenge or break another system?",
      "Are you designing the rules and letting strategy emerge, or scripting the outcome?",
      "What will players do with it that you're not expecting?",
    ],
  },
  {
    id: "onboarding",
    name: "Onboarding & Teaching",
    blurb: "How the player learns it without a wall of text.",
    appliesTo: ["mechanic", "system", "feature"],
    libraryEntryId: "lib-teaching",
    disciplineId: "onboarding",
    questions: [
      "How is this taught without a text wall — through play, safe failure, or affordance?",
      "Where does the player first use it, and is that first attempt safe?",
      "What's the one thing they must understand, and how do they learn it by doing?",
      "Could a new player discover it naturally, or will they miss it entirely?",
      "How do you confirm they've learned it before raising the stakes?",
    ],
  },
  {
    id: "edge",
    name: "Edge Cases & Failure",
    blurb: "Degenerate strategies, abuse, and what breaks it.",
    appliesTo: ["mechanic", "system", "feature"],
    libraryEntryId: "lib-second-order-design",
    disciplineId: "systems",
    questions: [
      "What's the most degenerate or boring strategy this enables — and why won't players just grind it?",
      "How could a griefer or bad actor abuse this against other players?",
      "What breaks at the boundaries — zero resources, maxed resources, disconnects, extreme inputs?",
      "What's the worst-case player experience this can produce?",
      "What assumption, if wrong, makes the whole thing fall apart?",
    ],
  },
  {
    id: "audience",
    name: "Accessibility & Audience",
    blurb: "Who it's for and who it might exclude.",
    appliesTo: ["mechanic", "system", "feature"],
    libraryEntryId: "lib-player-types",
    disciplineId: "accessibility",
    questions: [
      "Who is this for (which player type or audience), and what do they actually want from it?",
      "Does it rely on a single channel — color, sound, precise timing — that could exclude some players?",
      "What options would make it playable for more people without dumbing it down?",
      "Does it serve your target players more than it serves you?",
      "In a multiplayer setting, how does it shape the community or ecosystem?",
    ],
  },
  {
    id: "scope",
    name: "Scope & Production",
    blurb: "What it costs to build and how to de-risk it.",
    appliesTo: ["feature"],
    libraryEntryId: "lib-prototyping-playtesting",
    disciplineId: "scoping",
    questions: [
      "What's the riskiest assumption this depends on, and how could you test it most cheaply?",
      "What's the smallest prototype that would prove it's worth building?",
      "What would you cut first if you were over budget?",
      "How will you know — measurably — that it's working? What playtest would tell you?",
      "Does its value justify the cost to build it and to communicate it to the player?",
    ],
  },
  {
    id: "monetization",
    name: "Monetization & Ethics",
    blurb: "If money is involved: value, fairness, and dark patterns.",
    appliesTo: ["feature"],
    libraryEntryId: "lib-monetization-models",
    disciplineId: "monetization",
    questions: [
      "If this touches money, what's being sold — cosmetics and convenience, or power?",
      "Would a player feel respected if they saw the spreadsheet behind it?",
      "Does it lean on manufactured FOMO, obscured costs, or pay-to-skip-pain?",
      "Does it stay fair to players who don't spend?",
      "Is the core activity already rewarding before any monetization is layered on?",
    ],
  },
];

export function lensesForType(type: TriageType): TriageLens[] {
  return TRIAGE_LENSES.filter((l) => l.appliesTo.includes(type));
}

export function typeName(id: TriageType): string {
  return TRIAGE_TYPES.find((t) => t.id === id)?.name ?? id;
}
